/** 내 계정 — 조회·로그인 직후·로그아웃. 두 영역이 같은 규칙을 쓴다 */
import { auth } from '@geo-admin/shared/contract/api/index.ts';
import type { Me } from '@geo-admin/shared/contract/api/auth.ts';
import type { Realm } from '@geo-admin/shared/contract/session.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { keyOf, useApi } from './api.ts';
import { call } from './call.ts';
import { PATHS, pathsFor, realmOfRole } from './paths.ts';

export function useMe(enabled = true) {
  return useApi(auth.me, {}, { enabled });
}

export const meKey = (realm: Realm) => keyOf(auth.me, {}, realm);

/**
 * 로그인 성공 → 그 영역의 첫 화면으로. 서버는 로그인한 화면의 영역에 소속이 있을 때만 로그인시킨다.
 * ★ clear → 도착 영역 키로 me 심기 → 이동. invalidate 로 바꾸지 말 것 — staleTime 동안 옛 me 가 남아
 *   레이아웃이 옛 계정으로 판단한다. 키는 **지금 화면이 아니라 도착 영역**으로 만든다.
 */
export function useAfterLogin() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async (me: Me) => {
    qc.clear();
    const realm = realmOfRole(me.role);
    qc.setQueryData(meKey(realm), me);
    const p = PATHS[realm];
    if (me.mustChangePassword) return navigate({ to: p.password, replace: true });
    await navigate({ to: p.home, replace: true });
  };
}

/** 로그아웃 → 그 영역의 로그인 화면. 다른 영역의 로그인은 서버에서도 그대로다 */
export function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async (me: Me | undefined) => {
    await call(auth.logout).catch(() => {});
    qc.clear();
    await navigate({ to: pathsFor(me?.role).login, replace: true });
  };
}
